import { Button } from 'foundations-components/transitional/components/Button';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { useState, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import AccreditationItem from './AccreditationItem';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { useMediaQuery } from '../../../../utils/Styling';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useIsOnOwnProfile } from './profileUtils';
import { Flex } from 'foundations-components/components/Flex';
import { DownCaratIcon } from 'foundations-assets/icons/components/DownCaratIcon';
import { UpCaratIcon } from 'foundations-assets/icons/components/UpCaratIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const maxItemsToRenderWhenCollapsed = 2;
const EXPIRING_SOON_NOTICE_IN_DAYS = 90;
const renderAccreditationGridItem = (showCertificateModal, isMobile) => accreditation => /*#__PURE__*/_jsx(GridItem, {
  marginBlockStart: "600",
  size: isMobile ? 12 : 6,
  children: /*#__PURE__*/_jsx(AccreditationItem, {
    accreditation: accreditation,
    showCertificateModal: showCertificateModal
  })
}, accreditation.credentialId);
export const AccreditationGridSection = ({
  title,
  accreditations,
  showCertificateModals,
  onTransferAwardsClick,
  showTransferButton
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    isMobile
  } = useMediaQuery();
  const isOnOwnProfile = useIsOnOwnProfile();
  const activeAccreditations = useMemo(() => {
    return accreditations.filter(accreditation => {
      return accreditation.status === 'ACTIVE';
    });
  }, [accreditations]);
  const expiringSoonAccreditations = useMemo(() => {
    return accreditations.filter(accreditation => {
      return accreditation.status === 'EXPIRING_SOON';
    });
  }, [accreditations]);
  const activeAndExpiringSoonAccreditations = useMemo(() => {
    return [...expiringSoonAccreditations, ...activeAccreditations];
  }, [activeAccreditations, expiringSoonAccreditations]);
  const expiredAccreditations = useMemo(() => {
    return accreditations.filter(accreditation => {
      return accreditation.status === 'EXPIRED';
    });
  }, [accreditations]);
  const activeAndExpiringSoonAccreditationsTruncated = useMemo(() => {
    return activeAndExpiringSoonAccreditations.slice(0, maxItemsToRenderWhenCollapsed);
  }, [activeAndExpiringSoonAccreditations]);
  const expiredAccreditationsTruncated = useMemo(() => {
    return expiredAccreditations.slice(0, maxItemsToRenderWhenCollapsed);
  }, [expiredAccreditations]);
  const displayExpiringSoonAccreditationsWarning = isOnOwnProfile && expiringSoonAccreditations.length > 0;
  const showExpandButton = activeAndExpiringSoonAccreditations.length > maxItemsToRenderWhenCollapsed || expiredAccreditations.length > maxItemsToRenderWhenCollapsed;
  const hasCertifications = useMemo(() => {
    return accreditations.some(accreditation => accreditation.type === 'certification');
  }, [accreditations]);
  const shouldShowTransferButton = showTransferButton && hasCertifications && isOnOwnProfile;
  return /*#__PURE__*/_jsxs("section", {
    children: [/*#__PURE__*/_jsxs(Flex, {
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h4",
        children: title
      }), shouldShowTransferButton && /*#__PURE__*/_jsx(Button, {
        onClick: onTransferAwardsClick,
        responsive: false,
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "profilePage.AcademyProfile.accreditations.transferAwardsButton"
        })
      })]
    }), activeAndExpiringSoonAccreditations.length > 0 && /*#__PURE__*/_jsxs(View, {
      marginBlockStart: "600",
      children: [/*#__PURE__*/_jsxs(Text, {
        as: "h5",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "profilePage.AcademyProfile.accreditations.active"
        }), ' ', displayExpiringSoonAccreditationsWarning && !isMobile && /*#__PURE__*/_jsx(Tooltip, {
          placement: "right",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "profilePage.AcademyProfile.accreditations.expiringCertificationsWarning",
            options: {
              count: expiringSoonAccreditations.length,
              days: EXPIRING_SOON_NOTICE_IN_DAYS
            }
          }),
          children: /*#__PURE__*/_jsx("span", {
            "data-test-id": "expiring-soon-badge",
            style: {
              verticalAlign: 'text-bottom'
            },
            children: /*#__PURE__*/_jsx(Badge, {
              use: "count",
              children: expiringSoonAccreditations.length
            })
          })
        }), displayExpiringSoonAccreditationsWarning && isMobile && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("span", {
            children: "("
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "profilePage.AcademyProfile.accreditations.expiringCertificationsWarningMobile",
            options: {
              count: expiringSoonAccreditations.length
            }
          }), /*#__PURE__*/_jsx("span", {
            children: ")"
          })]
        })]
      }), /*#__PURE__*/_jsx(Grid, {
        children: (isExpanded ? activeAndExpiringSoonAccreditations : activeAndExpiringSoonAccreditationsTruncated).map(renderAccreditationGridItem(showCertificateModals, isMobile))
      })]
    }), expiredAccreditations.length > 0 && /*#__PURE__*/_jsxs(View, {
      marginBlockStart: "600",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "profilePage.AcademyProfile.accreditations.expired"
        })
      }), /*#__PURE__*/_jsx(Grid, {
        children: (isExpanded ? expiredAccreditations : expiredAccreditationsTruncated).map(renderAccreditationGridItem(showCertificateModals, isMobile))
      })]
    }), showExpandButton && /*#__PURE__*/_jsx("div", {
      style: {
        textAlign: 'center'
      },
      children: /*#__PURE__*/_jsx(Button, {
        marginBlockStart: "600",
        onClick: () => setIsExpanded(!isExpanded),
        use: "link",
        children: isExpanded ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "profilePage.AcademyProfile.accreditations.loadLessButton"
          }), /*#__PURE__*/_jsx(UpCaratIcon, {})]
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "profilePage.AcademyProfile.accreditations.loadMoreButton"
          }), /*#__PURE__*/_jsx(DownCaratIcon, {})]
        })
      })
    })]
  });
};